/**
 * Browser half of the first-party attribution-consent withdrawal (MEAS-02).
 *
 * The consent store in lib/consent/state.ts stays authoritative. This module
 * only (1) remembers the opaque intake references (`corr_<32 hex>`) that the
 * site's intake script writes onto a form as `data-crm-intake-correlation-id`
 * after a submission, and (2) when the consent store reports analytics as
 * withdrawn, sends those references to the server adapter. Sales approval never
 * implies consent, and nothing here ever grants or restores it.
 *
 * The references are kept under a strictly-necessary key (outside the
 * `seoh_`/`seoh-` optional-storage prefixes) because they exist only to honour a
 * privacy withdrawal; they hold no contact data and expire after 90 days.
 */

export const ATTRIBUTION_WITHDRAWAL_ENDPOINT = "/api/privacy/attribution-withdrawal";
export const ATTRIBUTION_REFERENCES_STORAGE_KEY = "seoh.attribution-withdrawal-refs.v1";
export const ATTRIBUTION_REFERENCE_ATTRIBUTE = "data-crm-intake-correlation-id";
const MAX_REFERENCES = 10;
const RETENTION_MS = 90 * 24 * 60 * 60 * 1_000;
const REFERENCE_PATTERN = /^corr_[a-f0-9]{32}$/;

type StoredReference = { readonly ref: string; readonly at: number };

type StorageLike = Pick<Storage, "getItem" | "setItem">;

export function readAttributionReferences(storage: StorageLike, now = Date.now()): StoredReference[] {
  try {
    const parsed = JSON.parse(storage.getItem(ATTRIBUTION_REFERENCES_STORAGE_KEY) || "[]") as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((item): item is StoredReference =>
        Boolean(item) && typeof item === "object" && REFERENCE_PATTERN.test(String((item as StoredReference).ref)) &&
        Number.isFinite((item as StoredReference).at) && now - (item as StoredReference).at < RETENTION_MS,
      )
      .slice(-MAX_REFERENCES);
  } catch {
    return [];
  }
}

export function rememberAttributionReference(storage: StorageLike, reference: unknown, now = Date.now()): boolean {
  if (typeof reference !== "string" || !REFERENCE_PATTERN.test(reference)) return false;
  const kept = readAttributionReferences(storage, now).filter((item) => item.ref !== reference);
  try {
    storage.setItem(ATTRIBUTION_REFERENCES_STORAGE_KEY, JSON.stringify([...kept, { ref: reference, at: now }].slice(-MAX_REFERENCES)));
    return true;
  } catch {
    return false;
  }
}

/** Builds the withdrawal request, or null when there is nothing to report. */
export function attributionWithdrawalRequest(storage: StorageLike, consent: unknown, now = Date.now()): { url: string; init: RequestInit } | null {
  if (!consent || typeof consent !== "object" || (consent as { analytics?: unknown }).analytics !== false) return null;
  const references = readAttributionReferences(storage, now).map((item) => item.ref);
  if (references.length === 0) return null;
  return {
    url: ATTRIBUTION_WITHDRAWAL_ENDPOINT,
    init: {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ references }),
      credentials: "omit",
      keepalive: true,
    },
  };
}

/** Wires the observer and consent listener in the browser. Safe to call once per page. */
export function installAttributionConsentWithdrawal(consentEventName: string): void {
  if (typeof window === "undefined" || typeof document === "undefined" || typeof MutationObserver === "undefined") return;
  const storage = window.localStorage;
  const capture = (element: Element) => rememberAttributionReference(storage, element.getAttribute(ATTRIBUTION_REFERENCE_ATTRIBUTE));
  document.querySelectorAll(`[${ATTRIBUTION_REFERENCE_ATTRIBUTE}]`).forEach(capture);
  new MutationObserver((records) => {
    for (const record of records) {
      if (record.type === "attributes" && record.target instanceof Element) capture(record.target);
    }
  }).observe(document.documentElement, { subtree: true, attributes: true, attributeFilter: [ATTRIBUTION_REFERENCE_ATTRIBUTE] });
  window.addEventListener(consentEventName, (event) => {
    const request = attributionWithdrawalRequest(storage, (event as CustomEvent).detail);
    if (request) void fetch(request.url, request.init).catch(() => undefined);
  });
}
